import React, { useState, useEffect, useRef } from 'react';
import { 
  Plus, Edit, Trash2, ShieldCheck, DollarSign, 
  Users, AlertCircle, MoreHorizontal, RefreshCw,
  Search, CheckCircle, XCircle
} from 'lucide-react';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import DataTable from '../common/DataTable';
import ConfirmationModal from '../common/ConfirmationModal';
import { useToast, ToastContainer } from '../common/Toast';
import doaService from '../../services/doaService';
import DoAFormModal from './doaFormModal';

const ActionDropdown = ({ config, onEdit, onDelete }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);
  
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  return (
    <div ref={dropdownRef} className="relative">
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        className="inline-flex items-center justify-center w-8 h-8 border border-gray-200 bg-white text-blue-500 rounded hover:bg-blue-50 transition-colors"
      >
        <MoreHorizontal size={16} />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-40 bg-white border border-gray-200 rounded-lg shadow-lg z-[1000]">
          <ul className="py-1">
            <li>
              <button
                onClick={() => { setIsOpen(false); onEdit(config); }}
                className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                <Edit size={14} className="mr-2" /> Edit Rule
              </button>
            </li>
            <li className="border-t border-gray-100">
              <button
                onClick={() => { setIsOpen(false); onDelete(config); }}
                className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50"
              >
                <Trash2 size={14} className="mr-2" /> Deactivate
              </button>
            </li>
          </ul>
        </div>
      )}
    </div>
  );
};

const DoAManagement = () => {
  const { success, error: showError, toasts, removeToast } = useToast();
  
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ totalRules: 0, activeRules: 0, highestLimit: 0 });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedConfig, setSelectedConfig] = useState(null);
  const [showWarningModal, setShowWarningModal] = useState(false);
  
  const loadData = async () => {
  setLoading(true);
  try {
    // Fetch both simultaneously for better performance
    const [configsRes, statsRes] = await Promise.all([
      doaService.getDoAConfigs(),
      doaService.getDoAStats()
    ]);

    if (configsRes.success) setConfigs(configsRes.data);
    if (statsRes.success) setStats(statsRes.data);
    
  } catch (err) {
    showError("Failed to load authority data");
  } finally {
    setLoading(false);
  }
};
  useEffect(() => {
    loadDoAConfigs();
    loadData();
  }, []);

  const loadDoAConfigs = async () => {
    setLoading(true);
    try {
      const response = await doaService.getDoAConfigs();
      if (response.success) {
        // Use a new array reference to force React to detect the change
        setConfigs([...response.data]); 
        
        // ... rest of your stats logic
      }
    } catch (err) {
      showError("Failed to load DoA configurations");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreate = () => {
    setSelectedConfig(null);
    setIsModalOpen(true);
  };

  const handleWarning = (config) => {
    setSelectedConfig(config);
    setIsModalOpen(true);
  };

  const handleSuccess = (msg) => {
  // 1. Force modal to close immediately
  setIsModalOpen(false);
  setSelectedConfig(null);

  // 2. Then handle notifications and data refresh
  success(msg);
  loadDoAConfigs(); 
};
  
  const handleDelete = async (config) => {
  // 1. Ask for confirmation
  const confirmMessage = `Are you sure you want to deactivate the approval rule for ${config.role?.name || 'this role'}?`;
  
  if (window.confirm(confirmMessage)) {
    try {
      // 2. Call the service
      // Note: Using the ID field based on your controller (_id)
      const response = await doaService.toggleDoAStatus(config._id);

      if (response.success) {
        // 3. Show success notification
        success("Authority rule deactivated successfully");
        
        // 4. Refresh the data from server
        loadDoAConfigs();
      } else {
        showError(response.message || "Failed to delete the rule");
      }
    } catch (err) {
      console.error("Delete error:", err);
      showError("An error occurred while trying to delete the rule.");
    }
  }
};

const handleToggleStatus = async (config) => {
  try {
    const newStatus = !config.isActive;
    const response = await doaService.updateDoAConfig(config._id, { isActive: newStatus });
    
    if (response.success) {
      success(`Rule ${newStatus ? 'activated' : 'deactivated'} successfully`);
      loadDoAConfigs();
    }
  } catch (err) {
    showError("Could not update rule status");
  }
};

  const handleOpenEdit = (config) => {
    if (!config.isActive) {
      setShowWarningModal(true);
      return;
    }
    setSelectedConfig(config);
    setIsModalOpen(true);
  };

  const columns = [
    {
      key: '__spacer__',
      label: '',
      width: '16px',
      sortable: false,
      render: () => <div style={{ width: '16px' }} />
    },
    {
      key: 'role',
      label: 'Role Name',
      render: (role) => (
        <div className="font-medium text-gray-900">
          {role?.displayName || role?.name || 'Unknown Role'}
          <div className="text-xs text-gray-500">Level: {role?.level || 'N/A'}</div>
        </div>
      )
    },
    {
      key: 'maxApprovalAmount',
      label: 'Approval Limit',
      render: (val, row) => (
        <div className="flex items-center font-semibold text-gray-700">
          <DollarSign size={14} className="text-gray-400 mr-1" />
          {val.toLocaleString()} <span className="ml-1 text-xs text-gray-400">{row.currency}</span>
        </div>
      )
    },
    {
      key: 'isActive',
      label: 'Status',
      render: (isActive) => (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
          isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
        }`}>
          {isActive ? <CheckCircle size={12} className="mr-1" /> : <XCircle size={12} className="mr-1" />}
          {isActive ? 'Active' : 'Inactive'}
        </span>
      )
    },
    {
      key: 'updatedAt',
      label: 'Last Modified',
      render: (date) => new Date(date).toLocaleDateString()
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (_, config) => (
        <ActionDropdown 
          config={config} 
          onEdit={handleOpenEdit} 
          onDelete={handleToggleStatus } 
        />
      )
    }
  ];

  return (
    <>
      <PageBreadcrumb />
      <PageHeader 
        title="Delegation of Authority (DoA)"
        description="Configure financial approval limits based on organizational roles."
        actions={[
          {
            label: 'Refresh',
            icon: <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />,
            onClick: loadDoAConfigs,
            variant: 'outline'
          },
          { 
            label: 'Add Rule', 
            icon: <Plus size={18} />, 
            onClick: handleOpenCreate, 
            variant: 'primary' }
        ]}
      />

      <div className="page-container px-6 pb-10">
        {/* Stats Section */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {[
            { label: 'Total Configured Roles', value: stats.totalRules, icon: Users, color: 'blue' },
            { label: 'Active Rules', value: stats.activeRules, icon: ShieldCheck, color: 'green' },
            { label: 'Highest Authority', value: `$${stats.highestLimit.toLocaleString()}`, icon: DollarSign, color: 'amber' }
          ].map((stat, i) => (
            <div key={i} className="bg-white p-5 border border-gray-200 rounded-lg shadow-sm flex items-center gap-4">
              <div className={`p-3 rounded-lg bg-${stat.color}-50 text-${stat.color}-600`}>
                <stat.icon size={24} />
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900">{stat.value}</div>
                <div className="text-sm text-gray-500">{stat.label}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Table Section */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
          <DataTable
            data={configs}
            columns={columns}
            loading={loading}
            searchable={true}
            showActions={false}
            emptyMessage="No DoA rules configured. Set limits for your organization roles."
          />
        </div>
      </div>
      <DoAFormModal 
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSuccess={handleSuccess}
        initialData={selectedConfig}
      />
      <ConfirmationModal
    isOpen={showWarningModal}
    onClose={() => setShowWarningModal(false)}
    onConfirm={() => setShowWarningModal(false)}
    title="Action Restricted"
    message="This rule is inactive. You must reactivate it before making any changes to the approval limits."
    confirmText="Understood"
    type="warning" // Assuming your component handles 'warning' for yellow styling
    showCancel={false} // If your modal supports hiding cancel for simple alerts
  />
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </>
  );
};

export default DoAManagement;